/**
 * FastAPI の 422 バリデーションエラー詳細の型
 */
interface ValidationErrorDetail {
    loc: (string | number)[];
    msg: string;
    type: string;
}

/**
 * API エラーレスポンスの型
 */
interface ApiErrorInfo {
    status: number;
    message: string;
    /** 422 バリデーションエラー時のフィールド単位の詳細 */
    fieldErrors?: { field: string; message: string; type: string }[];
}

/**
 * API エラーを構造化して保持するカスタムエラークラス
 */
export class ApiError extends Error {
    readonly status: number;
    readonly fieldErrors?: { field: string; message: string; type: string }[];

    constructor(info: ApiErrorInfo) {
        super(info.message);
        this.name = "ApiError";
        this.status = info.status;
        this.fieldErrors = info.fieldErrors;
    }

    /**
     * ログ出力用の構造化データを返す
     */
    toLogDetail(): Record<string, unknown> {
        const detail: Record<string, unknown> = {
            status: this.status,
            message: this.message,
        };
        if (this.fieldErrors && this.fieldErrors.length > 0) {
            detail.fieldErrors = this.fieldErrors;
        }
        return detail;
    }
}

/**
 * API レスポンスからエラーを生成する
 *
 * - 400: `{ detail: string }` → メッセージとして使用
 * - 422: `{ detail: ValidationErrorDetail[] }` → フィールド単位で構造化
 * - その他: ステータスコードベースの汎用メッセージ
 */
export async function parseApiError(response: Response, fallbackMessage: string): Promise<ApiError> {
    const status = response.status;

    try {
        const body = await response.json();

        // 422: FastAPI Pydantic バリデーションエラー
        if (status === 422 && Array.isArray(body.detail)) {
            const fieldErrors = (body.detail as ValidationErrorDetail[]).map((d) => ({
                field: d.loc.filter((l) => l !== "body").join("."),
                message: d.msg,
                type: d.type,
            }));
            const message = fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ");
            return new ApiError({ status, message: message || fallbackMessage, fieldErrors });
        }

        // 400, 404 など: { detail: string }
        if (typeof body.detail === "string") {
            return new ApiError({ status, message: body.detail });
        }

        return new ApiError({ status, message: fallbackMessage });
    } catch {
        return new ApiError({ status, message: fallbackMessage });
    }
}
